import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Modal } from './Modal';
import { Button } from './Button';
import { cn } from '../utils';

type ButtonVariant = React.ComponentProps<typeof Button>['variant'];

export interface ModalCloseButtonProps {
  onPress: () => void;
  isDisabled?: boolean;
  'aria-label'?: string;
  className?: string;
}

export function ModalCloseButton({
  onPress,
  isDisabled,
  'aria-label': ariaLabel = 'Close',
  className
}: ModalCloseButtonProps) {
  return (
    <Button
      isIconOnly
      variant="ghost"
      onPress={onPress}
      isDisabled={isDisabled}
      aria-label={ariaLabel}
      className={cn('shrink-0', className)}>
      <FontAwesomeIcon icon={faXmark} />
    </Button>
  );
}

export interface StandardModalHeaderProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  onClose?: () => void;
  closeDisabled?: boolean;
  closeAriaLabel?: string;
  className?: string;
  children?: React.ReactNode;
}

export function StandardModalHeader({
  title,
  description,
  onClose,
  closeDisabled,
  closeAriaLabel,
  className,
  children
}: StandardModalHeaderProps) {
  return (
    <Modal.Header className={cn('flex flex-row items-start justify-between gap-4', className)}>
      {children ?? (
        <div className="flex min-w-0 flex-col gap-1">
          {title != null &&
            (typeof title === 'string' ? <Modal.Heading>{title}</Modal.Heading> : title)}
          {description != null &&
            (typeof description === 'string' ? (
              <p className="text-sm font-normal text-default-500">{description}</p>
            ) : (
              description
            ))}
        </div>
      )}
      {onClose && (
        <ModalCloseButton
          onPress={onClose}
          isDisabled={closeDisabled}
          aria-label={
            closeAriaLabel ??
            (typeof title === 'string' ? `Close ${title.toLowerCase()} modal` : 'Close modal')
          }
        />
      )}
    </Modal.Header>
  );
}

export interface ModalActionConfig {
  label: string;
  onPress?: () => void;
  isPending?: boolean;
  isDisabled?: boolean;
  type?: 'button' | 'submit';
  variant?: ButtonVariant;
}

export interface ModalFormFooterProps {
  onCancel: () => void;
  cancelLabel?: string;
  cancelDisabled?: boolean;
  primary: ModalActionConfig;
  left?: React.ReactNode;
  className?: string;
}

export function ModalFormFooter({
  onCancel,
  cancelLabel = 'Cancel',
  cancelDisabled,
  primary,
  left,
  className
}: ModalFormFooterProps) {
  const actionButtons = (
    <>
      <Button variant="ghost" onPress={onCancel} isDisabled={cancelDisabled}>
        {cancelLabel}
      </Button>
      <Button
        variant={primary.variant ?? 'primary'}
        onPress={primary.onPress}
        type={primary.type}
        isPending={primary.isPending}
        isDisabled={primary.isDisabled}>
        {primary.label}
      </Button>
    </>
  );

  if (left) {
    return (
      <Modal.Footer className={cn('flex w-full items-center justify-between gap-2', className)}>
        {left}
        <div className="flex gap-2">{actionButtons}</div>
      </Modal.Footer>
    );
  }

  return (
    <Modal.Footer className={cn('justify-end gap-2', className)}>{actionButtons}</Modal.Footer>
  );
}

export interface ModalConfirmFooterProps {
  onCancel: () => void;
  cancelLabel?: string;
  cancelDisabled?: boolean;
  confirm: ModalActionConfig;
  className?: string;
}

export function ModalConfirmFooter({
  onCancel,
  cancelLabel = 'Cancel',
  cancelDisabled,
  confirm,
  className
}: ModalConfirmFooterProps) {
  return (
    <Modal.Footer className={cn('justify-end gap-2', className)}>
      <Button variant="ghost" onPress={onCancel} isDisabled={cancelDisabled}>
        {cancelLabel}
      </Button>
      <Button
        variant={confirm.variant ?? 'danger-soft'}
        onPress={confirm.onPress}
        type={confirm.type}
        isPending={confirm.isPending}
        isDisabled={confirm.isDisabled}>
        {confirm.label}
      </Button>
    </Modal.Footer>
  );
}

export interface ModalSplitFooterProps {
  left: React.ReactNode;
  right: React.ReactNode;
  className?: string;
}

export function ModalSplitFooter({ left, right, className }: ModalSplitFooterProps) {
  return (
    <Modal.Footer className={cn('flex w-full items-center justify-between gap-2', className)}>
      {left}
      {right}
    </Modal.Footer>
  );
}

export interface ModalPrimaryFooterProps {
  primary: ModalActionConfig;
  className?: string;
}

export function ModalPrimaryFooter({ primary, className }: ModalPrimaryFooterProps) {
  return (
    <Modal.Footer className={cn('justify-end gap-2', className)}>
      <Button
        variant={primary.variant ?? 'primary'}
        onPress={primary.onPress}
        type={primary.type}
        isPending={primary.isPending}
        isDisabled={primary.isDisabled}>
        {primary.label}
      </Button>
    </Modal.Footer>
  );
}
